import React, { useState } from 'react';
import { GitMerge, Clock, CheckCircle, XCircle, ArrowRight, AlertCircle } from 'lucide-react';
import T from 'Components/Helpers/translate';
import useAgentRunEvents from '../Chat/useAgentRunEvents';

/**
 * WEB-6800: a delegation rendered inline in the parent (delegating) conversation.
 *
 * Data comes from the parent assistant message's `metadata.toolTrace` — the
 * `delegateTask` tool result (see `extractDelegations`). The delegated work runs
 * as a headless child conversation; because that child run is owned by the same
 * user, its `agent.runStarted` / `agent.runFinalized` Pusher events reach us, and
 * (WEB-6800) carry the child `conversationId`, so the chip updates its status live
 * without ever needing the child runId up front. Clicking opens the child
 * conversation in the side drawer.
 */
export type DelegationData = {
	conversationId: number;
	agentId: number;
	agentName?: string;
	title?: string;
	rationale?: string;
	/** true = the agent delegated to itself (a background run); false = handed to another agent. */
	isSelf?: boolean;
	/** Initial status from the tool result (typically 'running'). */
	status: string;
};

const STATUS_ICON: Record<string, React.ReactNode> = {
	running: <Clock style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-yellow)' }} />,
	succeeded: <CheckCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-green)' }} />,
	failed: <XCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-red)' }} />,
	cancelled: <XCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-red)' }} />,
	timed_out: <XCircle style={{ width: 10, height: 10, flexShrink: 0, color: 'var(--ws-red)' }} />
};

/**
 * A delegated/background task rendered as a full-width, click-to-open banner
 * directly below the launching reply (WEB-7850) — so a task the user just
 * approved is unmistakable and lands in the right place in the timeline. Live
 * status (running → succeeded/failed) arrives via Pusher. The matching result
 * that comes back into the parent conversation is paired by task title, not a
 * colour code, so no per-run dot is needed here.
 */
const DelegationChip = ({
	delegation,
	onOpen
}: {
	delegation: DelegationData;
	onOpen: (delegation: DelegationData) => void;
}) => {
	const [status, setStatus] = useState<string>(delegation.status || 'running');

	useAgentRunEvents({
		onRunStarted: data => {
			if (data.conversationId === delegation.conversationId) setStatus('running');
		},
		onRunFinalized: data => {
			if (data.conversationId === delegation.conversationId) setStatus(data.status);
		}
	});

	const done = status !== 'running' && status !== 'queued';
	const agentName = delegation.agentName || T('assistant.delegation') || 'Delegated work';
	// Self-delegation = the agent offloading its own work to a background run;
	// delegation to a different agent = a hand-off. Label accordingly (click opens
	// it either way). isSelf===false → "Delegated to X"; otherwise (self, or older
	// chips without the flag) → "Background run".
	const label =
		delegation.isSelf === false
			? `${T('assistant.delegatedTo') || 'Delegated to'} ${agentName}${
					delegation.title ? `: ${delegation.title}` : ''
			  }`
			: `${T('assistant.backgroundRun') || 'Background run'}${delegation.title ? `: ${delegation.title}` : ''}`;

	return (
		<button
			type="button"
			className={`ws-delegation-launch-banner${done ? ' ws-delegation-launch-banner--done' : ''}`}
			onClick={() => onOpen(delegation)}
			title={delegation.rationale || label}
		>
			{STATUS_ICON[status] ?? <GitMerge style={{ width: 13, height: 13, flexShrink: 0 }} />}
			<span className="ws-launch-label">{label}</span>
			<ArrowRight style={{ width: 14, height: 14, flexShrink: 0, marginLeft: 'auto' }} />
		</button>
	);
};

/**
 * Extract delegations from an assistant message's metadata.toolTrace. Each
 * `delegateTask` tool-call's `output` is the stringified DelegateTaskResult
 * `{ ok, conversationId, agentId, agentName, status, rationale }`.
 */
export const extractDelegations = (metadata: unknown): DelegationData[] => {
	const trace = (metadata as { toolTrace?: unknown } | undefined)?.toolTrace;
	if (!Array.isArray(trace)) return [];
	const out: DelegationData[] = [];
	for (const entry of trace) {
		if (!entry || (entry as { name?: string }).name !== 'delegateTask') continue;
		const raw = (entry as { output?: unknown }).output;
		let parsed: Record<string, unknown> | null = null;
		try {
			parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>) ?? null;
		} catch {
			parsed = null;
		}
		if (!parsed || parsed.ok !== true || typeof parsed.conversationId !== 'number') continue;
		// Title comes from the tool OUTPUT (`parsed.title`) — the tool `input` is
		// persisted truncated (~500 chars) in toolTrace, so JSON.parsing it to
		// recover the title fails for anything but the shortest calls. The output
		// is short and always intact. Fall back to the input only for older
		// messages written before the output carried a title.
		let inputTitle: string | undefined;
		const rawInput = (entry as { input?: unknown }).input;
		try {
			const parsedInput =
				typeof rawInput === 'string' ? JSON.parse(rawInput) : (rawInput as Record<string, unknown>) ?? null;
			if (typeof parsedInput?.title === 'string') inputTitle = parsedInput.title;
		} catch {
			// truncated/invalid input — rely on the output title
		}
		out.push({
			conversationId: parsed.conversationId as number,
			agentId: Number(parsed.agentId) || 0,
			agentName: typeof parsed.agentName === 'string' ? parsed.agentName : undefined,
			title: typeof parsed.title === 'string' ? (parsed.title as string) : inputTitle,
			rationale: typeof parsed.rationale === 'string' ? parsed.rationale : undefined,
			isSelf: typeof parsed.isSelf === 'boolean' ? parsed.isSelf : undefined,
			status: typeof parsed.status === 'string' ? parsed.status : 'running'
		});
	}
	return out;
};

export type DelegationCompletionData = {
	childConversationId: number;
	agentId?: number;
	agentName?: string;
	/** true = the agent ran this itself in the background; false = a delegated agent did it. */
	isSelf?: boolean;
	/** The delegation's short title — pairs the result back to its launch banner. */
	title?: string;
	status: string;
	/** WEB-7699: approvalIds the child left as unapproved drafts. When present, the
	 *  delegated work produced proposals the user must still approve — the result
	 *  header shows a needs-approval indicator instead of a plain "done". */
	pendingApprovalIds?: number[];
};

/**
 * Extract the delegated-work completion marker an assistant message carries when
 * it's the summary posted back to the PARENT conversation after a delegated run
 * finished (see agentEngine `notifyParentOfDelegatedCompletion`).
 */
export const extractDelegationCompletion = (metadata: unknown): DelegationCompletionData | null => {
	const c = (metadata as { delegationCompletion?: unknown } | undefined)?.delegationCompletion as
		| {
				childConversationId?: unknown;
				agentId?: unknown;
				agentName?: unknown;
				isSelf?: unknown;
				title?: unknown;
				status?: unknown;
		  }
		| undefined;
	if (!c || typeof c.childConversationId !== 'number') return null;
	const cc = c as typeof c & { pendingApprovalIds?: unknown };
	const pendingApprovalIds = Array.isArray(cc.pendingApprovalIds)
		? cc.pendingApprovalIds.filter((id): id is number => typeof id === 'number')
		: undefined;
	return {
		childConversationId: c.childConversationId,
		agentId: typeof c.agentId === 'number' ? c.agentId : undefined,
		agentName: typeof c.agentName === 'string' ? c.agentName : undefined,
		isSelf: typeof c.isSelf === 'boolean' ? c.isSelf : undefined,
		title: typeof c.title === 'string' ? c.title : undefined,
		status: typeof c.status === 'string' ? c.status : 'succeeded',
		...(pendingApprovalIds && pendingApprovalIds.length ? { pendingApprovalIds } : {})
	};
};

/**
 * Banner shown directly above a delegated-work *result* message in the parent
 * conversation, so it's unmistakable the summary came back from a background
 * delegated run (rather than the parent agent talking). Click opens that child
 * conversation in the drawer.
 */
export const DelegationResultHeader = ({
	completion,
	onOpen,
	compact = false
}: {
	completion: DelegationCompletionData;
	onOpen: (delegation: DelegationData) => void;
	/** Small inline variant rendered in the message header row (next to name/time). */
	compact?: boolean;
}) => {
	const failed =
		completion.status === 'failed' || completion.status === 'cancelled' || completion.status === 'timed_out';
	// WEB-7699: the delegated run finished but left drafts the user must still
	// approve — nothing was committed. Surface that as its own amber indicator so
	// the user acts on it, instead of reading the summary as "done".
	const pendingCount = completion.pendingApprovalIds?.length ?? 0;
	const needsApproval = completion.status === 'draft_pending_approval' || pendingCount > 0;
	const open = () =>
		onOpen({
			conversationId: completion.childConversationId,
			agentId: completion.agentId ?? 0,
			agentName: completion.agentName,
			status: completion.status
		});

	if (needsApproval) {
		const label = T('assistant.delegationNeedsApproval') || 'Needs your approval';
		const hint = T('assistant.delegationNeedsApprovalOpen') || 'Open the drafts to review and approve';
		// Match the success path: pair to the launch banner by title (WEB-7850),
		// prefix the count when there's more than one pending draft.
		const base = pendingCount > 1 ? `${pendingCount} · ${label}` : label;
		const text = completion.title ? `${base}: ${completion.title}` : base;
		const alert = <AlertCircle style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--ws-yellow)' }} />;
		if (compact) {
			return (
				<button
					type="button"
					className="ws-delegation-result-tag"
					title={hint}
					onClick={open}
					style={{ color: 'var(--ws-yellow)' }}
				>
					{alert}
					<span>{text}</span>
				</button>
			);
		}
		return (
			<button
				type="button"
				className="ws-delegation-result-header"
				title={hint}
				onClick={open}
				style={{ color: 'var(--ws-yellow)' }}
			>
				{alert}
				<span className="ws-delegation-result-label">{text}</span>
				<ArrowRight style={{ width: 13, height: 13, flexShrink: 0, marginLeft: 'auto' }} />
			</button>
		);
	}
	const icon = failed ? (
		<XCircle style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--ws-red)' }} />
	) : (
		<CheckCircle style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--ws-green)' }} />
	);
	// isSelf===false → a different agent did the work ("Delegation result"); else
	// (self background run, or older messages without the flag) → "Background result".
	const isDelegated = completion.isSelf === false;
	const shortLabel = isDelegated
		? T('assistant.delegationResultShortDelegated') || 'Delegation result'
		: T('assistant.delegationResultShort') || 'Background result';
	// Pair the result to its launch banner by title (e.g. "Bakgrundsresultat:
	// Rensa försenade aktiviteter") instead of a colour dot. WEB-7850.
	const titledLabel = completion.title ? `${shortLabel}: ${completion.title}` : shortLabel;

	if (compact) {
		return (
			<button
				type="button"
				className="ws-delegation-result-tag"
				title={T('assistant.delegationResultOpen') || 'Open the delegated conversation'}
				onClick={open}
			>
				{icon}
				<span>{titledLabel}</span>
			</button>
		);
	}

	return (
		<button
			type="button"
			className="ws-delegation-result-header"
			title={T('assistant.delegationResultOpen') || 'Open the delegated conversation'}
			onClick={open}
		>
			{icon}
			<span className="ws-delegation-result-label">
				{isDelegated
					? `${T('assistant.delegationResultFrom') || 'Result from delegated work'}${
							completion.title ? `: ${completion.title}` : ''
					  }`
					: titledLabel}
			</span>
			{isDelegated && completion.agentName && (
				<>
					<span className="ws-delegation-result-dot">{'·'}</span>
					<span className="ws-delegation-result-agent">{completion.agentName}</span>
				</>
			)}
			<ArrowRight style={{ width: 13, height: 13, flexShrink: 0, marginLeft: 'auto' }} />
		</button>
	);
};

export default DelegationChip;
